import { Disclosure } from '@headlessui/react';
import { Card, H3 } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, PropsWithChildren, ReactNode } from 'react';

type CollapsibleCardProps = {
  title: ReactNode;
  /** Actions rendered on the right of the title row, shown only while expanded so they can't be triggered on a collapsed panel. */
  actions?: ReactNode;
  /** Shown on the right of the title row only while collapsed, to surface key info at a glance. */
  summary?: ReactNode;
  defaultOpen?: boolean;
};

/**
 * A Card whose body collapses behind its title. Built on the headless-ui
 * Disclosure primitive, adding a Card wrapper, a real heading, and an actions
 * slot that replaces the summary once expanded.
 */
const CollapsibleCard: FC<PropsWithChildren<CollapsibleCardProps>> = (
  props
) => {
  const { title, actions, summary, defaultOpen = false, children } = props;

  return (
    <Card>
      <Card.Body className="space-y-4">
        <Disclosure defaultOpen={defaultOpen}>
          {({ open }) => (
            <>
              {/* min-h matches the sm secondary Button used in the actions
                  slot, so the title and chevron don't shift vertically when the
                  collapsed summary is swapped for the taller expanded actions. */}
              <div className="flex min-h-11 items-center justify-between gap-4">
                <div className="min-w-0 grow">
                  <H3>
                    <Disclosure.Button className="flex w-full cursor-pointer items-center gap-2 bg-transparent p-0 text-left">
                      <UntitledIcon
                        name={open ? 'chevron-down' : 'chevron-right'}
                        className="h-5 w-5 shrink-0"
                      />
                      <span>{title}</span>
                    </Disclosure.Button>
                  </H3>
                </div>
                {open
                  ? actions && <div className="shrink-0">{actions}</div>
                  : summary && <div className="shrink-0">{summary}</div>}
              </div>

              <Disclosure.Panel className="space-y-4">
                {children}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </Card.Body>
    </Card>
  );
};

export default CollapsibleCard;
